import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import type { AppUser } from '@t90/common/supabase';
import { useSupabase, queryKeys } from '../provider';

// =====================================================
// AUTH STATE HOOK
// =====================================================

export function useAuth() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  // Subscribe to auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, _session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          queryClient.invalidateQueries({ queryKey: queryKeys.users.current() });
        }
        if (event === 'SIGNED_OUT') {
          queryClient.clear();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase, queryClient]);

  return useQuery({
    queryKey: ['auth', 'session'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =====================================================
// CURRENT USER HOOK
// =====================================================

export function useCurrentUser() {
  const supabase = useSupabase();
  const { data: session } = useAuth();

  return useQuery({
    queryKey: queryKeys.users.current(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          *,
          location:locations(*)
        `)
        .eq('id', session!.user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!session?.user.id,
  });
}

// =====================================================
// AUTH MUTATIONS
// =====================================================

interface SignInWithEmailParams {
  email: string;
  password: string;
}

interface SignUpWithEmailParams {
  email: string;
  password: string;
  fullName: string;
  role: AppUser['role'];
  locationId?: string;
}

export function useSignInWithEmail() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password }: SignInWithEmailParams) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Update last login
      if (data.user) {
        await supabase
          .from('app_users')
          .update({ last_login_at: new Date().toISOString() } as never)
          .eq('id', data.user.id);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.current() });
      toast.success('Signed in successfully');
    },
    onError: (error) => {
      toast.error(`Sign in failed: ${error.message}`);
    },
  });
}

export function useSignInWithGoogle() {
  const supabase = useSupabase();

  return useMutation({
    mutationFn: async (redirectTo?: string) => {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo ?? window.location.origin,
          queryParams: {
            hd: 'turn90.org', // Restrict to Turn90 Google Workspace domain
          },
        },
      });

      if (error) throw error;
      return data;
    },
    onError: (error) => {
      toast.error(`Google sign in failed: ${error.message}`);
    },
  });
}

export function useSignUp() {
  const supabase = useSupabase();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      fullName,
      role,
      locationId,
    }: SignUpWithEmailParams) => {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (authError) throw authError;

      // Create app_users record
      if (authData.user) {
        const { error: userError } = await supabase.from('app_users').insert({
          id: authData.user.id,
          email,
          full_name: fullName,
          role,
          location_id: locationId,
        } as never);

        if (userError) throw userError;
      }

      return authData;
    },
    onSuccess: () => {
      toast.success('Account created! Please check your email to verify.');
    },
    onError: (error) => {
      toast.error(`Sign up failed: ${error.message}`);
    },
  });
}

export function useSignOut() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.clear();
      toast.success('Signed out');
    },
    onError: (error) => {
      toast.error(`Sign out failed: ${error.message}`);
    },
  });
}

export function useResetPassword() {
  const supabase = useSupabase();

  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Password reset email sent');
    },
    onError: (error) => {
      toast.error(`Password reset failed: ${error.message}`);
    },
  });
}

export function useUpdatePassword() {
  const supabase = useSupabase();

  return useMutation({
    mutationFn: async (newPassword: string) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Password updated successfully');
    },
    onError: (error) => {
      toast.error(`Password update failed: ${error.message}`);
    },
  });
}
